/**
 * @file Settings mutation helpers.
 */

import {
    deleteDoc as firebaseDeleteDoc,
    deleteField as firebaseDeleteField,
    doc as firebaseDoc,
    serverTimestamp as firebaseServerTimestamp,
    setDoc as firebaseSetDoc,
    updateDoc as firebaseUpdateDoc,
    writeBatch as firebaseWriteBatch,
} from "https://www.gstatic.com/firebasejs/12.19.0/firebase-firestore.js";

import { splitRuntimeConfigForPersistence as configuredSplitRuntimeConfigForPersistence } from "../../app/data/configAssembler.js";
import { db as configuredDb } from "../../app/firebase/firebase.js";
import {
    checkDataUsageCallable as configuredCheckDataUsageCallable,
    migrateDataCallable as configuredMigrateDataCallable,
} from "../../app/firebase/callables.js";
import {
    appData as configuredAppData,
    clearTempTeamsData as configuredClearTempTeamsData,
    rebaseTempConfigBaselineData as configuredRebaseTempConfigBaselineData,
    setTempUserDeactivationDraft as configuredSetTempUserDeactivationDraft,
    state as configuredState,
    tempConfigBaselineData as configuredTempConfigBaselineData,
    tempConfigData as configuredTempConfigData,
    tempTeamsData as configuredTempTeamsData,
    tempUserDeactivationDraft as configuredTempUserDeactivationDraft,
    tempUsersData as configuredTempUsersData,
} from "../../app/state/state.js";
import { openConfirmationModal as configuredOpenConfirmationModal } from "../../shared/ui/confirmationModal.js";
import { saveUserDeactivationDraft as configuredSaveUserDeactivationDraft } from "./userLifecycleApi.js";
import { getUserSettingsEdits, preflightUserSettingsChanges,
    saveTrustedUserSettings } from "./userAccessSettings.js";
import { getOrderingListsValidationError } from "../ordering/orderingLists.js";

/**
 * Creates Settings API helpers.
 * @param {object} deps Dependency bag.
 * @returns {object} Settings API helpers.
 */
export function createSettingsApi({
    appData,
    checkDataUsageCallable,
    clearTempTeamsData,
    db,
    deleteDoc,
    deleteField,
    doc,
    migrateDataCallable,
    openConfirmationModal,
    rebaseTempConfigBaselineData = () => {},
    saveUserDeactivationDraft,
    serverTimestamp,
    setDoc,
    setTempUserDeactivationDraft,
    splitRuntimeConfigForPersistence,
    state,
    getSettingsDrafts,
    updateDoc,
    writeBatch,
}) {
/**
 * Compares persisted configuration values without treating object key order as
 * a user change.
 *
 * @param {*} left First value.
 * @param {*} right Second value.
 * @returns {boolean} Whether the values are equivalent.
 */
function areConfigValuesEqual(left, right) {
    if (Object.is(left, right)) {return true;}
    if (!left || !right || typeof left !== "object" || typeof right !== "object") {
        return false;
    }
    if (left instanceof Date || right instanceof Date) {
        return left instanceof Date && right instanceof Date &&
            left.getTime() === right.getTime();
    }
    if (Array.isArray(left) || Array.isArray(right)) {
        return Array.isArray(left) && Array.isArray(right) &&
            left.length === right.length &&
            left.every((value, index) => areConfigValuesEqual(value, right[index]));
    }

    const leftKeys = Object.keys(left).sort();
    const rightKeys = Object.keys(right).sort();
    if (leftKeys.length !== rightKeys.length) {return false;}
    return leftKeys.every((key, index) => key === rightKeys[index] &&
        areConfigValuesEqual(left[key], right[key]));
}

/**
 * Builds a merge-safe top-level document patch from a Settings draft.
 *
 * @param {object | null | undefined} draft Current Settings document shape.
 * @param {object | null | undefined} baseline Entry snapshot document shape.
 * @param {Function} createDeleteField Firestore field-deletion sentinel factory.
 * @returns {object} Changed top-level fields and intentional removals only.
 */
function buildConfigDocumentPatch(draft, baseline, createDeleteField) {
    const current = draft || {};
    const original = baseline || {};
    const keys = new Set([...Object.keys(original), ...Object.keys(current)]);
    const patch = {};

    keys.forEach((key) => {
        const hasCurrentValue = Object.hasOwn(current, key) &&
            current[key] !== undefined;
        const hasOriginalValue = Object.hasOwn(original, key) &&
            original[key] !== undefined;

        if (!hasCurrentValue) {
            if (hasOriginalValue) {
                patch[key] = createDeleteField();
            }
            return;
        }

        if (!hasOriginalValue || !areConfigValuesEqual(current[key], original[key])) {
            patch[key] = current[key];
        }
    });

    return patch;
}

/**
 * Saves configuration changes AND user permission changes to Firestore.
 * Returns true if successful, false otherwise.
 */
async function saveSettingsChanges() {
    const {
        tempConfigBaselineData: draftConfigBaseline,
        tempConfigData: draftConfigData,
        tempUsersData: draftUsersData,
        tempTeamsData: draftTeamsData,
        tempUserDeactivationDraft: draftUserDeactivation,
    } = getSettingsDrafts();

    if (!draftConfigData && !draftUsersData && !draftTeamsData && !draftUserDeactivation) {
        openConfirmationModal('Error', 'No settings data found to save.', async () => { }, false, 'OK');
        return false;
    }

    try {
        if (!await preflightUserSettingsChanges(draftUsersData || [])) {return false;}
        // 1. Save Config (if changed)
        if (draftConfigData) {
            const {
                mainConfig,
                notificationConfig,
            } = splitRuntimeConfigForPersistence(draftConfigData);
            const {
                mainConfig: baselineMainConfig,
                notificationConfig: baselineNotificationConfig,
            } = splitRuntimeConfigForPersistence(
                draftConfigBaseline || appData.config || {},
            );
            const mainPatch = buildConfigDocumentPatch(
                mainConfig,
                baselineMainConfig,
                deleteField,
            );
            const notificationPatch = buildConfigDocumentPatch(
                notificationConfig,
                baselineNotificationConfig,
                deleteField,
            );

            if (Object.hasOwn(mainConfig, "orderingLists")) {
                const listValidationError = getOrderingListsValidationError(
                    mainConfig.orderingLists,
                );
                if (listValidationError) {
                    openConfirmationModal(
                        "Ordering lists",
                        listValidationError,
                        async () => {},
                        false,
                        "OK",
                    );
                    return false;
                }
            }

            if (Object.keys(mainPatch).length || Object.keys(notificationPatch).length) {
                const batch = writeBatch(db);
                if (Object.keys(mainPatch).length) {
                    batch.set(doc(db, 'config', 'main'), mainPatch, { merge: true });
                }
                if (Object.keys(notificationPatch).length) {
                    batch.set(
                        doc(db, 'config', 'notifications'),
                        notificationPatch,
                        { merge: true },
                    );
                }
                await batch.commit();
                rebaseTempConfigBaselineData(draftConfigData);
            }
        }

        // 2. Save only edited user fields through the trusted Settings callable.
        for (const edit of getUserSettingsEdits(draftUsersData || [])) {
            await saveTrustedUserSettings(edit);
        }

        // 3. Save Teams
        if (draftTeamsData && Array.isArray(draftTeamsData)) {
            const teamPromises = [];
            const currentTeams = Array.isArray(appData.teams) ? appData.teams : [];
            const nextTeamIds = new Set();

            draftTeamsData.forEach((tempTeam) => {
                const teamId = tempTeam.id;
                if (!teamId) {return;}
                nextTeamIds.add(teamId);

                const originalTeam = currentTeams.find(team => team.id === teamId);
                const payload = {
                    name: tempTeam.name || 'Untitled Team',
                    members: Array.isArray(tempTeam.members) ? tempTeam.members : [],
                    visible: tempTeam.visible !== false,
                    order: Number.isFinite(Number(tempTeam.order)) ? Number(tempTeam.order) : 999,
                };

                if (!originalTeam) {
                    teamPromises.push(setDoc(doc(db, 'teams', teamId), payload));
                    return;
                }

                const originalPayload = {
                    name: originalTeam.name || 'Untitled Team',
                    members: Array.isArray(originalTeam.members) ? originalTeam.members : [],
                    visible: originalTeam.visible !== false,
                    order: Number.isFinite(Number(originalTeam.order)) ? Number(originalTeam.order) : 999,
                };

                if (JSON.stringify(originalPayload) !== JSON.stringify(payload)) {
                    teamPromises.push(updateDoc(doc(db, 'teams', teamId), payload));
                }
            });

            currentTeams.forEach((team) => {
                if (!nextTeamIds.has(team.id)) {
                    teamPromises.push(deleteDoc(doc(db, 'teams', team.id)));
                }
            });

            if (teamPromises.length > 0) {
                await Promise.all(teamPromises);
            }

            try {
                appData.teams = structuredClone(draftTeamsData);
            } catch (error) {
                console.error("Failed to refresh appData.teams after save:", error);
                appData.teams = Array.isArray(draftTeamsData) ? [...draftTeamsData] : [];
            }
        }

        if (draftUserDeactivation?.draft?.targetUserId) {
            const savedDraft = await saveUserDeactivationDraft(
                draftUserDeactivation.draft.targetUserId,
                {
                    defaultReplacementUserId: draftUserDeactivation.draft.defaultReplacementUserId || null,
                    selections: draftUserDeactivation.draft.selections || {},
                    operation: draftUserDeactivation.draft.operation,
                    draftId: draftUserDeactivation.draft.id,
                }
            );
            setTempUserDeactivationDraft(savedDraft);
        }

        // console.log('Settings saved successfully.'); // Debug log - commented out
        state.hasUnsavedChanges = false;

        clearTempTeamsData();

        return true;

    } catch (error) {
        console.error("Error saving settings:", error);
        const errorMessage = (error.code === 'internal' || !error.message)
            ? 'Something went wrong. Please try again later.'
            : `Failed to save settings: ${error.message}`;
        openConfirmationModal('Error', errorMessage, async () => { }, false, 'OK');
        return false; // Signal failure
    }
}

async function triggerNonAdminLogoutWave(logoutAt = new Date().toISOString()) {
    const configRef = doc(db, 'config', 'main');
    await setDoc(configRef, { nonAdminLogoutAt: logoutAt }, { merge: true });
    return logoutAt;
}


/**
 * Updates the current logged-in user's document in Firestore.
 * Used for updating user preferences, notification settings, etc.
 * @param {object} dataToUpdate - An object containing the fields to update (e.g., { notificationSettings: {...} }).
 */
async function updateUserDocument(dataToUpdate) {
    if (!state.currentUser || !state.currentUser.uid) {
        console.error('updateUserDocument: No current user found.');
        return;
    }
    if (!dataToUpdate || Object.keys(dataToUpdate).length === 0) {
        console.error('updateUserDocument: No data provided to update.');
        return;
    }

    const docRef = doc(db, 'users', state.currentUser.uid);
    try {
        // Add an 'updatedAt' timestamp to this update
        const finalData = {
            ...dataToUpdate,
            updatedBy: state.currentUser.uid,
            updatedAt: serverTimestamp(), // Use server timestamp for authoritative time
        };
        await updateDoc(docRef, finalData);
    } catch (error) {
        console.error(`Error updating user document ${state.currentUser.uid}:`, error);
    }
}

/**
 * Calls the backend to migrate configured option fields.
 * @param {string} type - Migration target key.
 * @param {string} oldValue - The ID to find.
 * @param {string} newValue - The ID to replace it with.
 */
async function migrateData(type, oldValue, newValue) {
    if (!type || !oldValue || !newValue) {
        throw new Error("Missing migration parameters.");
    }

    try {
        const result = await migrateDataCallable({
            type: type,
            oldValue: oldValue,
            newValue: newValue
        });
        return result.data;
    } catch (error) {
        console.error("Migration error:", error);
        throw error;
    }
}

/**
 * Checks how many items are using a specific configured value before migration.
 * Used to show users how many items will be affected by a data migration.
 * @param {string} type - Migration target key.
 * @param {string} value - The configured value ID to check.
 * @returns {Promise<number>} The count of items using this value, or -1 on error.
 */
async function checkDataUsage(type, value) {
    try {
        const result = await checkDataUsageCallable({ type, value });
        return result.data.count;
    } catch (error) {
        console.error("Error checking usage:", error);
        return -1; // Return -1 on error to indicate "Unsure"
    }
}


    return {
        saveSettingsChanges,
        triggerNonAdminLogoutWave,
        updateUserDocument,
        migrateData,
        checkDataUsage,
    };
}

const configuredSettingsApi = createSettingsApi({
    appData: configuredAppData,
    checkDataUsageCallable: configuredCheckDataUsageCallable,
    clearTempTeamsData: configuredClearTempTeamsData,
    db: configuredDb,
    deleteDoc: firebaseDeleteDoc,
    deleteField: firebaseDeleteField,
    doc: firebaseDoc,
    getSettingsDrafts: () => ({
        tempConfigBaselineData: configuredTempConfigBaselineData,
        tempConfigData: configuredTempConfigData,
        tempUsersData: configuredTempUsersData,
        tempTeamsData: configuredTempTeamsData,
        tempUserDeactivationDraft: configuredTempUserDeactivationDraft,
    }),
    migrateDataCallable: configuredMigrateDataCallable,
    openConfirmationModal: configuredOpenConfirmationModal,
    rebaseTempConfigBaselineData: configuredRebaseTempConfigBaselineData,
    saveUserDeactivationDraft: configuredSaveUserDeactivationDraft,
    serverTimestamp: firebaseServerTimestamp,
    setDoc: firebaseSetDoc,
    setTempUserDeactivationDraft: configuredSetTempUserDeactivationDraft,
    splitRuntimeConfigForPersistence: configuredSplitRuntimeConfigForPersistence,
    state: configuredState,
    updateDoc: firebaseUpdateDoc,
    writeBatch: firebaseWriteBatch,
});

export const saveSettingsChanges = configuredSettingsApi.saveSettingsChanges;
export const triggerNonAdminLogoutWave = configuredSettingsApi.triggerNonAdminLogoutWave;
export const updateUserDocument = configuredSettingsApi.updateUserDocument;
export const migrateData = configuredSettingsApi.migrateData;
export const checkDataUsage = configuredSettingsApi.checkDataUsage;
